import React, { useContext, useMemo, useState } from "react";
import { LanguageContext } from "../../../context/languageContext";
import HomeLocalStateAdapter from "../../../pages/home/localStateAdapter";
import { HomeModel } from "../../../pages/home/types";
import RegistrationAdapter from "../../../pages/registration/adapter";
import { useScrollToTop } from "../../../pages/shared/hooks/useScrollToTop";
import logging from "../../../util/logging";
import GTM from "../../../util/tracking/gtm";
import {
  ApplicationChapterId,
  ApplicationFlows,
  ApplicationFlowSteps,
} from "../authenticated/application-flow/types";
import ApplicationFlowProvider from "../authenticated/application-flow/applicationFlowContext";
import { ProductContext } from "../../../context/product-context/productContext";
import { Product } from "../../../context/product-context/product";
import { homeDefaults } from "../../../pages/home/defaults";

const log = logging("anonymous/index.ts");

const AnonymousSection = (): React.JSX.Element => {
  const { language } = useContext(LanguageContext);
  const { selectedProduct, setSelectedProduct } = useContext(ProductContext);
  const [homeModel, setHomeModel] = useState<HomeModel>(homeDefaults);

  const [page, setPage] = useState<ApplicationFlowSteps>(() => {
    GTM.trackVirtualPageView(
      ApplicationFlowSteps.COMPANY,
      selectedProduct?.productRef,
      language,
    );
    return ApplicationFlowSteps.COMPANY;
  });

  const setStepAndTrack = useMemo(
    () =>
      (step: ApplicationFlowSteps): void => {
        setPage(step);
        GTM.trackVirtualPageView(step, selectedProduct?.productRef, language);
      },
    [selectedProduct, language],
  );

  useScrollToTop(page);

  let pageToRender = null;

  switch (page) {
    case ApplicationFlowSteps.REGISTRATION:
      pageToRender = (
        <RegistrationAdapter
          homeModel={homeModel}
          previous={() => setStepAndTrack(ApplicationFlowSteps.COMPANY)}
        />
      );
      break;
    case ApplicationFlowSteps.COMPANY:
      pageToRender = (
        <HomeLocalStateAdapter
          homeModel={homeModel}
          onNext={(homeModelToSave: HomeModel) => {
            setHomeModel(homeModelToSave);
            setStepAndTrack(ApplicationFlowSteps.REGISTRATION);
          }}
          handleOnProductFamilySelection={(product: Product): void => {
            setSelectedProduct(product);
            setStepAndTrack(ApplicationFlowSteps.COMPANY);
          }}
        />
      );
      break;
    default:
      log.warn(`Unknown step in anonymous section: ${page}`);
      pageToRender = (
        <HomeLocalStateAdapter
          homeModel={homeModel}
          onNext={(homeModelToSave: HomeModel) => {
            setHomeModel(homeModelToSave);
            setStepAndTrack(ApplicationFlowSteps.REGISTRATION);
          }}
          handleOnProductFamilySelection={(product: Product): void => {
            setSelectedProduct(product);
            setStepAndTrack(ApplicationFlowSteps.COMPANY);
          }}
        />
      );
  }
  return (
    <ApplicationFlowProvider
      flow={ApplicationFlows.FIRST_BAA}
      activeChapter={ApplicationChapterId.Company}
      setStep={setStepAndTrack}
    >
      {pageToRender}
    </ApplicationFlowProvider>
  );
};

export default AnonymousSection;
